import { getOwner, registerDestructor, invokeHelper, getValue, setHelperManager, setOwner, helperCapabilities, createCache, destroy, associateDestroyableChild, waitForPromise, isDestroyed, isDestroying } from './environment.js';
import { ApolloError, NetworkStatus } from '@apollo/client/core';
export { gql } from '@apollo/client/core';
import { tracked } from '@glimmer/tracking';
import { equal } from '@wry/equality';
import '@glimmer/env';

const CLIENTS = new WeakMap();
const DEFAULT_CLIENT_ID = 'default';
function setClient(context, client, clientId = DEFAULT_CLIENT_ID) {
  const owner = getOwner(context);

  if (!owner) {
    throw new Error('Unable to find owner from the given context in glimmer-apollo setClient');
  }

  if (!CLIENTS.has(owner)) {
    CLIENTS.set(owner, new Map());
  }

  CLIENTS.get(owner)?.set(clientId, client);
  registerDestructor(context, () => {
    clearClient(context, clientId);
  });
}
function getClient(context, clientId = DEFAULT_CLIENT_ID) {
  const owner = getOwner(context);

  if (!owner) {
    throw new Error('Unable to find owner from the given context in glimmer-apollo getClient');
  }

  const client = CLIENTS.get(owner)?.get(clientId);

  if (!client) {
    throw new Error(`Apollo client with id ${clientId} has not been set yet, use setClient(new ApolloClient({ ... }, '${clientId}')) to define it`);
  }

  return client;
}
function clearClients(context) {
  const owner = getOwner(context);

  if (!owner) {
    throw new Error('Unable to find owner from the given context in glimmer-apollo clearClients');
  }

  const bucket = CLIENTS.get(owner);
  bucket?.forEach(client => {
    client.clearStore();
  });
  bucket?.clear();
}
function clearClient(context, clientId = DEFAULT_CLIENT_ID) {
  const owner = getOwner(context);

  if (!owner) {
    throw new Error('Unable to find owner from the given context in glimmer-apollo clearClient');
  }

  const bucket = CLIENTS.get(owner);
  const client = bucket?.get(clientId);

  if (client) {
    client.clearStore();
  }

  bucket?.delete(clientId);
}

function normalizeArgs(args) {
  if (Array.isArray(args)) {
    return {
      positional: args,
      named: {}
    };
  }

  if ('positional' in args || 'named' in args) {
    return {
      positional: args.positional || [],
      named: args.named || {}
    };
  }

  if (typeof args === 'object') {
    return {
      named: args,
      positional: []
    };
  }

  return args;
}

function useUnproxiedResource(destroyable, definition, args) {
  let resource;
  return {
    get value() {
      if (!resource) {
        resource = invokeHelper(destroyable, definition, // eslint-disable-line
        () => {
          return normalizeArgs(args?.() || {});
        });
      }

      return getValue(resource); // eslint-disable-line
    }

  };
}
function useResource(destroyable, definition, args) {
  const target = useUnproxiedResource(destroyable, definition, args);
  return new Proxy(target, {
    get(target, key) {
      const instance = target.value;
      const value = Reflect.get(instance, key, instance);
      return typeof value === 'function' ? value.bind(instance) : value;
    },

    ownKeys(target) {
      return Reflect.ownKeys(target.value);
    },

    getOwnPropertyDescriptor(target, key) {
      return Reflect.getOwnPropertyDescriptor(target.value, key);
    }

  });
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _classPrivateFieldBase(receiver, privateKey) {
  if (!Object.prototype.hasOwnProperty.call(receiver, privateKey)) {
    throw new TypeError("attempted to use private field on non-instance");
  }

  return receiver;
}

var id = 0;
function _classPrivateFieldKey(name) {
  return "__private_" + id++ + "_" + name;
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
function __decorate(decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}

class Resource {
  constructor(ownerOrThunk, args) {
    if (typeof ownerOrThunk === 'function') {
      // @ts-expect-error This is naughty.
      return {
        definition: this.constructor,
        args: ownerOrThunk
      };
    }

    setOwner(this, ownerOrThunk);
    this.args = args;
  } // eslint-disable-next-line @typescript-eslint/no-empty-function


  setup() {}

}

class ResourceManager {
  constructor(owner) {
    this.capabilities = helperCapabilities('3.23', {
      hasValue: true,
      hasDestroyable: true
    });
    this.owner = owner;
  }

  createHelper(Class, args) {
    const {
      update,
      teardown
    } = Class.prototype;
    const hasUpdate = typeof update === 'function';
    const hasTeardown = typeof teardown === 'function';
    const owner = this.owner;
    let instance;
    let cache;

    if (hasUpdate) {
      cache = createCache(() => {
        if (instance === undefined) {
          instance = setupInstance(cache, Class, owner, args, hasTeardown);
        } else {
          instance.update(); // eslint-disable-line
        }

        return instance;
      });
    } else {
      cache = createCache(() => {
        if (instance !== undefined) {
          destroy(instance);
        }

        instance = setupInstance(cache, Class, owner, args, hasTeardown);
        return instance;
      });
    }

    return cache;
  }

  getValue(cache) {
    const instance = getValue(cache);
    return instance;
  }

  getDestroyable(cache) {
    return cache;
  } //eslint-disable-next-line


  getDebugName(fn) {
    return fn.name || '(anonymous function)';
  }

}

function setupInstance(cache, Class, owner, args, hasTeardown) {
  const instance = new Class(owner, args);
  associateDestroyableChild(cache, instance);
  instance.setup();

  if (hasTeardown) {
    registerDestructor(instance, () => instance.teardown()); // eslint-disable-line
  }

  return instance;
}

setHelperManager(owner => new ResourceManager(owner), Resource);

function hasFastBoot(obj) {
  return Object.prototype.hasOwnProperty.call(obj, 'FastBoot');
}

function ownerHasLookup(owner //eslint-disable-next-line
) {
  return !!(owner && 'lookup' in owner);
}

function getFastboot(ctx) {
  if (typeof self != 'undefined' && hasFastBoot(self) && typeof self.FastBoot !== 'undefined') {
    const owner = getOwner(ctx);

    if (ownerHasLookup(owner) && typeof owner.lookup === 'function') {
      return owner.lookup('service:fastboot');
    }
  }

  return undefined;
}
function createPromise() {
  let resolvePromise;
  let rejectPromise;
  const promise = new Promise((resolve, reject) => {
    resolvePromise = resolve;
    rejectPromise = reject;
  });
  return [promise, resolvePromise, rejectPromise]; //eslint-disable-line
}
function settled(promise) {
  return new Promise(resolve => {
    if (promise) {
      promise.then(() => resolve()).catch(() => resolve());
    } else {
      resolve();
    }
  });
}

function ownKeys$2(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$2(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$2(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$2(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

var _onComplete$2 = /*#__PURE__*/_classPrivateFieldKey("onComplete");

var _onError$2 = /*#__PURE__*/_classPrivateFieldKey("onError");

var _handleOnCompleteOrOnError$1 = /*#__PURE__*/_classPrivateFieldKey("handleOnCompleteOrOnError");

class MutationResource extends Resource {
  constructor(...args) {
    super(...args);
    Object.defineProperty(this, _handleOnCompleteOrOnError$1, {
      value: _handleOnCompleteOrOnError2$1
    });
    Object.defineProperty(this, _onError$2, {
      value: _onError2$2
    });
    Object.defineProperty(this, _onComplete$2, {
      value: _onComplete2$2
    });
    this.loading = false;
    this.called = false;
  }

  async mutate(variables, overrideOptions = {}) {
    this.loading = true;
    const [mutation, originalOptions] = this.args.positional;

    const options = _objectSpread$2(_objectSpread$2({}, originalOptions), overrideOptions);

    const client = getClient(this, options.clientId);

    if (!variables) {
      variables = originalOptions?.variables;
    } else if (variables && originalOptions?.variables) {
      variables = _objectSpread$2(_objectSpread$2({}, originalOptions.variables), variables);
    }

    this.promise = waitForPromise(client.mutate(_objectSpread$2(_objectSpread$2({
      mutation
    }, options), {}, {
      variables
    }))).then(result => {
      _classPrivateFieldBase(this, _onComplete$2)[_onComplete$2](result);

      return this.data;
    }).catch(error => {
      _classPrivateFieldBase(this, _onError$2)[_onError$2](error);

      return error;
    });
    return this.promise;
  }

  settled() {
    return settled(this.promise);
  }

}

function _onComplete2$2(result) {
  const {
    data,
    errors
  } = result;
  const error = errors && errors.length > 0 ? new ApolloError({
    graphQLErrors: errors
  }) : undefined;
  this.data = data;
  this.error = error;

  _classPrivateFieldBase(this, _handleOnCompleteOrOnError$1)[_handleOnCompleteOrOnError$1]();
}

function _onError2$2(error) {
  this.error = error;
  this.data = undefined;

  _classPrivateFieldBase(this, _handleOnCompleteOrOnError$1)[_handleOnCompleteOrOnError$1]();
}

function _handleOnCompleteOrOnError2$1() {
  this.loading = false;
  this.called = true; // We want to avoid calling the callbacks when this is destroyed.
  // If the resource is destroyed, the callback context might not be defined anymore.

  if (isDestroyed(this) || isDestroying(this)) {
    return;
  }

  const [, options] = this.args.positional;
  const {
    onComplete,
    onError
  } = options || {};
  const {
    data,
    error
  } = this;

  if (onComplete && !error) {
    onComplete(data);
  } else if (onError && error) {
    onError(error);
  }
}

__decorate([tracked], MutationResource.prototype, "loading", void 0);

__decorate([tracked], MutationResource.prototype, "called", void 0);

__decorate([tracked], MutationResource.prototype, "error", void 0);

__decorate([tracked], MutationResource.prototype, "data", void 0);

__decorate([tracked], MutationResource.prototype, "promise", void 0);

/* eslint-disable @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/explicit-function-return-type */
class ObservableResource extends Resource {
  constructor(...args) {
    super(...args);

    this.refetch = variables => this.observable?.refetch(variables);

    this.fetchMore = fetchMoreOptions => this.observable?.fetchMore(fetchMoreOptions);

    this.updateQuery = mapFn => this.observable?.updateQuery(mapFn);

    this.startPolling = pollInterval => {
      this.observable?.startPolling(pollInterval);
    };

    this.stopPolling = () => {
      this.observable?.stopPolling();
    };

    this.subscribeToMore = options => this.observable?.subscribeToMore(options);
  }

  _setObservable(observable) {
    this.observable = observable;
  }

}

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys$1(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

var _subscription$1 = /*#__PURE__*/_classPrivateFieldKey("subscription");

var _previousPositionalArgs$1 = /*#__PURE__*/_classPrivateFieldKey("previousPositionalArgs");

var _firstPromiseReject = /*#__PURE__*/_classPrivateFieldKey("firstPromiseReject");

var _onComplete$1 = /*#__PURE__*/_classPrivateFieldKey("onComplete");

var _onError$1 = /*#__PURE__*/_classPrivateFieldKey("onError");

var _handleOnCompleteOrOnError = /*#__PURE__*/_classPrivateFieldKey("handleOnCompleteOrOnError");

class QueryResource extends ObservableResource {
  constructor(...args) {
    super(...args);
    Object.defineProperty(this, _handleOnCompleteOrOnError, {
      value: _handleOnCompleteOrOnError2
    });
    Object.defineProperty(this, _onError$1, {
      value: _onError2$1
    });
    Object.defineProperty(this, _onComplete$1, {
      value: _onComplete2$1
    });
    this.loading = true;
    this.networkStatus = NetworkStatus.loading;
    Object.defineProperty(this, _subscription$1, {
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, _previousPositionalArgs$1, {
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, _firstPromiseReject, {
      writable: true,
      value: void 0
    });
  }

  /** @internal */
  async setup() {
    _classPrivateFieldBase(this, _previousPositionalArgs$1)[_previousPositionalArgs$1] = this.args.positional;
    const [query, options] = this.args.positional;
    const client = getClient(this, options?.clientId);
    this.loading = true;
    const fastboot = getFastboot(this);

    if (fastboot && fastboot.isFastBoot && options && options.ssr === false) {
      return;
    }

    let [promise, firstResolve, firstReject] = createPromise(); // eslint-disable-line prefer-const

    _classPrivateFieldBase(this, _firstPromiseReject)[_firstPromiseReject] = firstReject;
    this.promise = promise;
    const observable = client.watchQuery(_objectSpread$1({
      query
    }, options || {}));

    this._setObservable(observable);

    _classPrivateFieldBase(this, _subscription$1)[_subscription$1] = observable.subscribe(result => {
      _classPrivateFieldBase(this, _onComplete$1)[_onComplete$1](result);

      if (firstResolve) {
        firstResolve();
        firstResolve = undefined;
      }
    }, error => {
      _classPrivateFieldBase(this, _onError$1)[_onError$1](error);

      if (_classPrivateFieldBase(this, _firstPromiseReject)[_firstPromiseReject]) {
        _classPrivateFieldBase(this, _firstPromiseReject)[_firstPromiseReject]();

        _classPrivateFieldBase(this, _firstPromiseReject)[_firstPromiseReject] = undefined;
      }
    });
    waitForPromise(promise).catch(() => {// We catch by default as the promise is only meant to be used
      // as an indicator if the query is being initially fetched.
    });

    if (fastboot && fastboot.isFastBoot && options && options.ssr !== false) {
      fastboot.deferRendering(promise);
    }
  }
  /** @internal */


  update() {
    if (!equal(_classPrivateFieldBase(this, _previousPositionalArgs$1)[_previousPositionalArgs$1], this.args.positional)) {
      this.teardown();
      this.setup();
    }
  }
  /** @internal */


  teardown() {
    if (_classPrivateFieldBase(this, _subscription$1)[_subscription$1]) {
      _classPrivateFieldBase(this, _subscription$1)[_subscription$1].unsubscribe();
    }

    if (_classPrivateFieldBase(this, _firstPromiseReject)[_firstPromiseReject]) {
      _classPrivateFieldBase(this, _firstPromiseReject)[_firstPromiseReject]();

      _classPrivateFieldBase(this, _firstPromiseReject)[_firstPromiseReject] = undefined;
    }
  }

  settled() {
    return settled(this.promise);
  }

}

function _onComplete2$1(result) {
  const {
    loading,
    errors,
    data,
    networkStatus
  } = result;
  let {
    error
  } = result;
  error = errors && errors.length > 0 ? new ApolloError({
    graphQLErrors: errors
  }) : undefined;
  this.loading = loading;
  this.data = data;
  this.networkStatus = networkStatus;
  this.error = error;

  _classPrivateFieldBase(this, _handleOnCompleteOrOnError)[_handleOnCompleteOrOnError]();
}

function _onError2$1(error) {
  if (!Object.prototype.hasOwnProperty.call(error, 'graphQLErrors')) {
    error = new ApolloError({
      networkError: error
    });
  }

  this.loading = false;
  this.data = undefined;
  this.networkStatus = NetworkStatus.error;
  this.error = error;

  _classPrivateFieldBase(this, _handleOnCompleteOrOnError)[_handleOnCompleteOrOnError]();
}

function _handleOnCompleteOrOnError2() {
  // We want to avoid calling the callbacks when this is destroyed.
  // If the resource is destroyed, the callback context might not be defined anymore.
  if (isDestroyed(this) || isDestroying(this)) {
    return;
  }

  const [, options] = this.args.positional;
  const {
    onComplete,
    onError
  } = options || {};
  const {
    data,
    error
  } = this;

  if (onComplete && !error) {
    onComplete(data);
  } else if (onError && error) {
    onError(error);
  }
}

__decorate([tracked], QueryResource.prototype, "loading", void 0);

__decorate([tracked], QueryResource.prototype, "error", void 0);

__decorate([tracked], QueryResource.prototype, "data", void 0);

__decorate([tracked], QueryResource.prototype, "networkStatus", void 0);

__decorate([tracked], QueryResource.prototype, "promise", void 0);

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

var _subscription = /*#__PURE__*/_classPrivateFieldKey("subscription");

var _previousPositionalArgs = /*#__PURE__*/_classPrivateFieldKey("previousPositionalArgs");

var _onNextResult = /*#__PURE__*/_classPrivateFieldKey("onNextResult");

var _onError = /*#__PURE__*/_classPrivateFieldKey("onError");

var _onComplete = /*#__PURE__*/_classPrivateFieldKey("onComplete");

class SubscriptionResource extends Resource {
  constructor(...args) {
    super(...args);
    Object.defineProperty(this, _onComplete, {
      value: _onComplete2
    });
    Object.defineProperty(this, _onError, {
      value: _onError2
    });
    Object.defineProperty(this, _onNextResult, {
      value: _onNextResult2
    });
    this.loading = true;
    Object.defineProperty(this, _subscription, {
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, _previousPositionalArgs, {
      writable: true,
      value: void 0
    });
  }

  /** @internal */
  async setup() {
    _classPrivateFieldBase(this, _previousPositionalArgs)[_previousPositionalArgs] = this.args.positional;
    const [query, options] = this.args.positional;
    const client = getClient(this, options?.clientId);
    this.loading = true;
    const fastboot = getFastboot(this);

    if (fastboot && fastboot.isFastBoot && options && options.ssr === false) {
      return;
    }

    let [promise, firstResolve, firstReject] = createPromise(); // eslint-disable-line prefer-const

    this.promise = promise;
    const observable = client.subscribe(_objectSpread({
      query
    }, options || {}));
    _classPrivateFieldBase(this, _subscription)[_subscription] = observable.subscribe({
      next: result => {
        if (isDestroyed(this) || isDestroying(this)) {
          return;
        }

        _classPrivateFieldBase(this, _onNextResult)[_onNextResult](result);

        if (firstResolve) {
          firstResolve();
          firstResolve = undefined;
        }
      },
      error: error => {
        if (isDestroyed(this) || isDestroying(this)) {
          return;
        }

        _classPrivateFieldBase(this, _onError)[_onError](error);

        if (firstReject) {
          firstReject();
          firstReject = undefined;
        }
      },
      complete: () => {
        if (isDestroyed(this) || isDestroying(this)) {
          return;
        }

        _classPrivateFieldBase(this, _onComplete)[_onComplete]();
      }
    });
    waitForPromise(promise).catch(() => {// We catch by default as the promise is only meant to be used
      // as an indicator if the query is being initially fetched.
    });

    if (fastboot && fastboot.isFastBoot && options && options.ssr !== false) {
      fastboot.deferRendering(promise);
    }
  }
  /** @internal */


  update() {
    if (!equal(_classPrivateFieldBase(this, _previousPositionalArgs)[_previousPositionalArgs], this.args.positional) || !_classPrivateFieldBase(this, _subscription)[_subscription]) {
      this.teardown();
      this.setup();
    }
  }
  /** @internal */


  teardown() {
    if (_classPrivateFieldBase(this, _subscription)[_subscription]) {
      _classPrivateFieldBase(this, _subscription)[_subscription].unsubscribe();
    }
  }

  settled() {
    return settled(this.promise);
  }

}

function _onNextResult2(result) {
  this.loading = false;
  this.error = undefined;
  const {
    data
  } = result;

  if (data == null) {
    this.data = undefined;
  } else {
    this.data = data;
  }

  const [, options] = this.args.positional;
  const {
    onData
  } = options || {};

  if (onData) {
    onData(this.data);
  }
}

function _onError2(error) {
  if (!Object.prototype.hasOwnProperty.call(error, 'graphQLErrors')) {
    error = new ApolloError({
      networkError: error
    });
  }

  this.loading = false;
  this.data = undefined;
  this.error = error;
  const [, options] = this.args.positional;
  const {
    onError
  } = options || {};

  if (onError) {
    onError(this.error);
  }
}

function _onComplete2() {
  this.loading = false;
  const [, options] = this.args.positional;
  const {
    onComplete
  } = options || {};

  if (onComplete) {
    onComplete();
  }

  if (_classPrivateFieldBase(this, _subscription)[_subscription]) {
    _classPrivateFieldBase(this, _subscription)[_subscription].unsubscribe();

    _classPrivateFieldBase(this, _subscription)[_subscription] = undefined;
  }
}

__decorate([tracked], SubscriptionResource.prototype, "loading", void 0);

__decorate([tracked], SubscriptionResource.prototype, "error", void 0);

__decorate([tracked], SubscriptionResource.prototype, "data", void 0);

__decorate([tracked], SubscriptionResource.prototype, "promise", void 0);

function useQuery(parentDestroyable, args) {
  return useResource(parentDestroyable, QueryResource, args);
}
function useMutation(parentDestroyable, args) {
  return useResource(parentDestroyable, MutationResource, args);
}
function useSubscription(parentDestroyable, args) {
  return useResource(parentDestroyable, SubscriptionResource, args);
}

export { clearClient, clearClients, getClient, setClient, useMutation, useQuery, useSubscription };
